import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, ExternalLink, Briefcase } from "lucide-react";
import { MapLink } from "@/components/MapLink";
import AndringsforslagButton from "@/components/AndringsforslagButton";
import MediaLink from "@/components/MediaLink";
import SocialLinks from "@/components/SocialLinks";
import EntryFootnote from "@/components/EntryFootnote";
import ExpandableBeskrivning from "@/components/ExpandableBeskrivning";
import GalleriStrip from "@/components/GalleriStrip";
import { useTranslatedObjects } from "@/hooks/useTranslatedContent";
import { usePageMeta } from "@/utils/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForetagIBygden() {
  usePageMeta({
    title: "Företag – vidalv.live",
    description: "Lokala företag, tjänster och hantverk i bygden vid Fjällsjöälven. Hitta kontakt, öppettider och var verksamheten finns.",
    path: "/foretag",
  });
  const [foretag, setForetag] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/praktiskt/tjanstekort`).then(r => { setForetag(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const [foretagT] = useTranslatedObjects(foretag, ["foretagsnamn", "beskrivning", "tjanster"]);
  // Etappe 4.10 — gemensam alfabetisk lista (svensk locale, Å/Ä/Ö efter Z). Ingen ort-gruppering på publika sidan.
  const foretagSorted = [...foretagT].sort((a, b) =>
    (a.foretagsnamn || "").localeCompare(b.foretagsnamn || "", "sv", { sensitivity: "base" })
  );

  return (
    <div data-testid="foretag-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Företag</p>
      <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-2">Företag i bygden</h1>
      <p className="text-sm text-bark mb-10 max-w-2xl leading-relaxed">
        Företag, hantverkare och verksamheter i Fjällsjöbygden. Stöd det lokala &ndash; fråga grannen först.
      </p>

      {loading && <p className="text-bark py-8">Laddar...</p>}

      {!loading && (
        <div className="flex flex-col gap-4">
          <section data-testid="foretag-list">
            <div className="flex flex-col gap-3">
              {foretagSorted.map((f) => (
                <ForetagCard key={f.id} foretag={f} />
              ))}
            </div>
          </section>

          <p className="text-xs text-bark leading-relaxed max-w-2xl mt-8">
            "Fråga om RUT" och "Fråga om ROT" betyder att företaget uppger att de hanterar arbete som kan vara relevant för avdrag. Om avdraget gäller i ditt fall beror på typ av arbete och dina personliga förutsättningar. Kontakta företaget och Skatteverket vid frågor.
          </p>
          <p className="text-xs text-bark/50">Senast kontrollerat: 16 april 2026. Kontrollera alltid uppgifterna direkt med respektive företag.</p>
        </div>
      )}
    </div>
  );
}

function ForetagCard({ foretag: f }) {
  return (
    <div data-testid={`foretag-${f.id}`} className="bg-parchment border border-warm-border rounded-sm p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Briefcase size={16} className="text-pine shrink-0" />
            <h3 className="font-heading text-xl font-semibold text-ink">{f.foretagsnamn}</h3>
          </div>
          {f.ort && <p className="text-xs text-bark flex items-center gap-1 mb-2 ml-6"><MapPin size={13} /> {f.ort}</p>}
          <div className="ml-6 mb-3">
            <ExpandableBeskrivning kort={f.kortbeskrivning} full={f.beskrivning} testId={`foretag-${f.id}-besk`} />
          </div>
          <div className="flex flex-wrap items-center gap-3 ml-6">
            {f.kontakt_telefon && (
              <a href={`tel:${f.kontakt_telefon.replace(/\s/g, "")}`} data-testid={`foretag-phone-${f.id}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-pine hover:text-pine-hover no-underline">
                <Phone size={14} /> {f.kontakt_telefon}
              </a>
            )}
            {f.kontakt_epost && (
              <a href={`mailto:${f.kontakt_epost}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-falu hover:text-falu-hover no-underline">
                <Mail size={14} /> {f.kontakt_epost}
              </a>
            )}
            {f.extern_lank && (
              <a href={f.extern_lank.startsWith("http") ? f.extern_lank : `https://${f.extern_lank}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-falu hover:text-falu-hover no-underline">
                <ExternalLink size={14} /> {f.kalltext || "Webbplats"}
              </a>
            )}
            <MapLink name={f.foretagsnamn} />
          </div>
          {/* Etappe 4.1 — universella sociala länkar (Web/FB/IG/YT). Visas endast om fyllda. */}
          <div className="ml-6">
            <SocialLinks
              webbplats={f.webbplats}
              facebook_url={f.facebook_url}
              instagram_url={f.instagram_url}
              youtube_url={f.youtube_url}
            />
          </div>
          {/* Etappe 4.11 — galleri (visas endast om bilder finns) */}
          {Array.isArray(f.galleri) && f.galleri.length > 0 && (
            <div className="ml-6 mt-3">
              <GalleriStrip bilder={f.galleri} galleriAudio={f.galleri_audio} alt={f.foretagsnamn} />
            </div>
          )}
          <div className="ml-6">
            <MediaLink media_url={f.media_url} media_typ={f.media_typ} media_beskrivning={f.media_beskrivning} />
          </div>
          <div className="ml-6">
            <EntryFootnote
              taggar={f.taggar}
              kalla={f.kalla || f.kalltext}
              senast={f.senast_andrad || f.senast_kontrollerad}
              uppdaterad_av={f.andrad_av}
              testIdPrefix={`foretag-${f.id}`}
            />
          </div>
          <div className="mt-4 pt-3 border-t border-warm-border/70 ml-6">
            <AndringsforslagButton typ="foretag" referensId={f.id} referensNamn={f.foretagsnamn} />
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {f.fraga_om_rut && <span className="text-xs font-medium bg-pine/10 text-pine px-2 py-1 rounded">Fråga om RUT</span>}
          {f.fraga_om_rot && <span className="text-xs font-medium bg-falu/10 text-falu px-2 py-1 rounded">Fråga om ROT</span>}
        </div>
      </div>
    </div>
  );
}
